/**
 * 67.68 (the owner, Oct 19: "also can put someone on dropship sound fx"): what a dropship sortie and a pod drop are heard
 * doing, and when — the rules; `dropshipFx.ts` voices the events, `dropshipSounds.ts` drives both and the ship's
 * continuous voice (dropshipMix.ts) in play. Everything is read from the sim, nothing written: a flight's pose and motion
 * are pure functions of the tick (core/dropship.ts `dropshipPose`, `dropshipKinematics`), its events sit at its own ticks
 * (`landTick`, `exitTick`, the riders' exits, `liftTick`, `goneTick`), and a pod drop lands at its `dropTick`.
 *
 * - **The approach:** the continuous voice — a rising roar from far, the turbines' pitch falling as it passes (by its
 *   closing speed), the note turning as the nacelles tilt down to hover (`voiceState`).
 * - **Touchdown (troops):** the gear's thump on the ground (by its material), the container's set-down clunk and its
 *   latches; the ramp's hydraulic whine as it opens and its clang down; boots down the ramp, one rider at a time (each a
 *   respawn); a short warning tone before lift-off; the spool-up; a sortie's ship (it carries its container away) shuts
 *   the ramp first, a standing container's latches let go as it lifts.
 * - **A vehicle:** the cable winch as it lowers from the hover, the wheels' thump, the cables' release, the clamps.
 * - **Departure:** the spool-up and the climb are the voice's (its thrust, the wash), the pivot or the loop its motion.
 * - **Pods:** the falling whistle before the drop, each pod's impact and its dust in a ring, the hatches popping.
 * - **The wave ship's pad** (DECISIONS 207 (1), `GameState.shipPads`, core's `parkedShips`): between runs it idles there,
 *   its engines low, heard near the base only (`padState`, `padFade`); a run spools up from that idle on the take-off run
 *   (the warning beeping as it goes), climbing at full thrust; back home it slows onto the pad on the approach's thrust,
 *   its gear thumps down and it settles to the idle.
 *
 * Pure: no audio, no state written.
 */
import { DROPSHIP, DROPSHIP_ANCHORS, PHYS_DT, PODS } from '../constants';
import type { DropshipState } from './dropshipMix';

/** The events' timing (seconds): the latches after touchdown, the warning's lead before lift-off and its beeps, the spool-up's lead, a pod's fall heard. */
export const DROPSHIP_FX = {
  latches: [0.45, 0.6], warnLead: 1.8, warnBeeps: 3, warnEvery: 0.38, spoolLead: 1.4,
  podFall: 2.4, podStagger: 0.07, hatchAfter: 0.7, hatchStagger: 0.11,
};

export type DropshipEventKind =
  | 'touch' | 'containerDown' | 'latch' | 'rampOpen' | 'rampDown' | 'rampShut' | 'boots' | 'warn' | 'containerLetGo'
  | 'winch' | 'wheels' | 'cableRelease' | 'clamps'
  | 'podWhistle' | 'podImpact' | 'podHatch';

/** An event: its tick, its kind, how long it lasts (s, for the long ones: the winch, the ramp, the whistle), and which (a rider, a pod). */
export interface DropshipEvent { tick: number; kind: DropshipEventKind; seconds?: number; index?: number }

/** What a flight's events are read from (core's DropshipFlight carries these; the sheet's sortie makes its own). */
export interface FlightTimes {
  payload: 'troops' | 'vehicle' | 'gunship';
  landTick: number; exitTick: number; liftTick: number; goneTick: number;
  riders: number;
  /** a sortie's ship carries its container away (the ramp shuts first); else the container stays on the ground */
  sortie?: boolean;
  /** 207 (1): the side's wave ship — it takes off from its pad at `calledTick` and lands back on it at `goneTick` */
  fromPad?: boolean;
  calledTick?: number;
}

const ticks = (s: number) => Math.round(s / PHYS_DT);

/** A flight's events, in tick order (worked out once a flight: its ticks are fixed when it is called). */
export function flightEvents(f: FlightTimes): DropshipEvent[] {
  const F = DROPSHIP_FX, A = DROPSHIP_ANCHORS.seconds, out: DropshipEvent[] = [];
  if (f.payload === 'gunship') return out;   // (63.13: it never lands; its guns' sounds are its own)
  if (f.payload === 'troops') {
    out.push({ tick: f.landTick, kind: 'touch' }, { tick: f.landTick + ticks(0.12), kind: 'containerDown' });
    F.latches.forEach((s, i) => out.push({ tick: f.landTick + ticks(s), kind: 'latch', index: i }));
    out.push({ tick: f.landTick, kind: 'rampOpen', seconds: (f.exitTick - f.landTick) * PHYS_DT }, { tick: f.exitTick, kind: 'rampDown' });
    for (let i = 0; i < f.riders; i++) out.push({ tick: f.exitTick + i * Math.max(1, ticks(DROPSHIP.exitSeconds)), kind: 'boots', index: i });
    if (f.sortie) out.push({ tick: f.liftTick - ticks(A.rampShut + 0.3), kind: 'rampShut', seconds: A.rampShut });
    else out.push({ tick: f.liftTick, kind: 'containerLetGo' });
  } else {
    out.push({ tick: f.landTick, kind: 'winch', seconds: (f.exitTick - f.landTick) * PHYS_DT }, { tick: f.exitTick, kind: 'wheels' });
    out.push({ tick: f.exitTick + ticks(A.cableRelease), kind: 'cableRelease' }, { tick: f.exitTick + ticks(A.cableRelease + A.clampsOpen), kind: 'clamps' });
  }
  for (let i = 0; i < F.warnBeeps; i++) out.push({ tick: f.liftTick - ticks(F.warnLead) + ticks(i * F.warnEvery), kind: 'warn', index: i });
  // (207 (1): the pad's take-off, the warning beeping while it spools; home, its gear down on the pad)
  if (f.fromPad && f.calledTick !== undefined) for (let i = 0; i < F.warnBeeps; i++) out.push({ tick: f.calledTick + ticks(i * F.warnEvery), kind: 'warn', index: i });
  if (f.fromPad) out.push({ tick: f.goneTick, kind: 'touch' });
  return out.sort((a, b) => a.tick - b.tick);
}

/** A pod drop's events: the whistle over its fall, each pod's impact a little apart, their hatches popping after. */
export function podEvents(dropTick: number, pods: number): DropshipEvent[] {
  const F = DROPSHIP_FX, n = Math.max(1, Math.min(pods, PODS.squad)), out: DropshipEvent[] = [{ tick: dropTick - ticks(F.podFall), kind: 'podWhistle', seconds: F.podFall }];
  for (let i = 0; i < n; i++) out.push({ tick: dropTick + ticks(i * F.podStagger), kind: 'podImpact', index: i });
  for (let i = 0; i < n; i++) out.push({ tick: dropTick + ticks(F.hatchAfter + i * F.hatchStagger), kind: 'podHatch', index: i });
  return out;
}

/** The events due in (from, to]: those a frame crossed (none when the clock stood still). */
export function eventsCrossed(events: readonly DropshipEvent[], from: number, to: number): DropshipEvent[] {
  return to > from ? events.filter(e => e.tick > from && e.tick <= to) : [];
}

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/**
 * 207 (1): the wave ship on its pad between runs — its engines low (`idle` thrust), settling there from the landing's
 * (`settleFrom`) over `settle` seconds; heard near the base only: as any ship to `near` people, faded out by `range`. A
 * run spools up from the idle over `DROPSHIP_FX.spoolLead`.
 */
/**
 * The pad's voice: settling from `settleFrom` to the `idle` over `settle` s after it lands; 69.26 (the owner: *"dropship idle
 * whine is annoying, they can rest turned off then turn back on before flight"*): then on the idle `hold` s more and wound
 * down to off over `windDown` s; set out again, its engines wind up from where they were over `spoolUp` s, through its
 * take-off run's first seconds, before it climbs.
 */
export const PAD_FX = { idle: 0.2, settleFrom: 0.9, settle: 4, near: 120, range: 240, hold: 3, windDown: 5, spoolUp: 3 };

/** 69.26: how far its engines are turning `since` seconds after it landed on its pad (1 … 0; null: standing there from the start, off). */
export function padSpool(since: number | null): number {
  if (since === null) return 0;
  const k = clamp((since - PAD_FX.settle - PAD_FX.hold) / PAD_FX.windDown, 0, 1);
  return 1 - k * k * (3 - 2 * k);
}

/** The pad's voice `since` seconds after the ship landed on it (null: it stood there from the start — 69.26: off). */
export function padState(since: number | null): DropshipState {
  const k = since === null ? 1 : clamp(since / PAD_FX.settle, 0, 1);
  return { thrust: PAD_FX.idle + (PAD_FX.settleFrom - PAD_FX.idle) * (1 - k * (2 - k)), tilt: 1, heightPeople: 0, closing: 0, spool: padSpool(since) };
}

/** × the pad's voice at `people` from the listener: 1 to `near`, 0 from `range` (the voice is let go there). */
export function padFade(people: number): number {
  return clamp((PAD_FX.range - people) / (PAD_FX.range - PAD_FX.near), 0, 1);
}

/**
 * The continuous voice's state at `tick` (dropshipMix `DropshipState`), from the flight's phase and motion: in, the
 * cruise's thrust rising as it slows to the hover, the nacelles tilting down as it slows; down, a troop ship idles on its
 * gear (a vehicle's hovers, full), spooling up over `spoolLead` before it lifts; out, full while it climbs, easing to the
 * cruise. `speed` is along its heading (people a second), `height` over the ground (people), `closing` toward the listener.
 * `pad` (207 (1), the wave ship: seconds since it set out from its pad, its acceleration along its heading, people a
 * second²): its take-off run climbs at the departure's thrust, spooled up from the pad's idle; its landing run back onto
 * the pad slows on the approach's. 69.26: `pad.spool0`, how far its engines were turning when it was called (0: off on its
 * pad) — wound up from there over PAD_FX.spoolUp.
 */
export function voiceState(phase: 'in' | 'down' | 'loiter' | 'out', payload: 'troops' | 'vehicle' | 'gunship', tick: number, liftTick: number, speed: number, height: number, closing: number, pad?: { since: number; accel: number; spool0?: number }): DropshipState {
  const slow = clamp(1 - Math.abs(speed) / DROPSHIP.speedPeople, 0, 1);
  const approach = 0.68 + 0.24 * slow, departure = 1 - 0.2 * clamp((Math.abs(speed) / DROPSHIP.speedPeople - 0.5) * 2, 0, 1);
  let thrust: number;
  if (phase === 'in' || phase === 'loiter') thrust = pad && pad.accel > PAD_RUN ? Math.min(departure, PAD_FX.idle + (1 - PAD_FX.idle) * clamp(pad.since / DROPSHIP_FX.spoolLead, 0, 1)) : approach;
  else if (phase === 'down') {
    const base = payload === 'vehicle' ? 0.92 : 0.5, lead = DROPSHIP_FX.spoolLead / PHYS_DT;
    thrust = base + (1 - base) * clamp(1 - (liftTick - tick) / lead, 0, 1);
  } else thrust = pad && pad.accel < -PAD_RUN ? approach : departure;
  const spool = pad && pad.spool0 !== undefined ? clamp(pad.spool0 + pad.since / PAD_FX.spoolUp, 0, 1) : 1;
  return { thrust, tilt: phase === 'down' ? 1 : slow, heightPeople: Math.max(0, height), closing, ...(spool < 1 ? { spool } : {}) };
}
/** The acceleration (people a second²) that marks a pad ship's take-off or landing run (they run at ~5; the cruise at 0). */
const PAD_RUN = 0.5;

/**
 * Each event's level (× the ship's voice gain, DROPSHIP_VOICE.gain), how near it is loud and how far it is heard (people):
 * the metal and the thumps carry, a latch or a boot is a near sound, a pod's impact carries furthest.
 */
export const FX_HEARD: Record<DropshipEventKind, { level: number; reach: number; range: number }> = {
  touch: { level: 0.75, reach: 30, range: 400 }, wheels: { level: 0.8, reach: 30, range: 400 },
  containerDown: { level: 0.6, reach: 25, range: 300 }, latch: { level: 0.45, reach: 10, range: 60 }, containerLetGo: { level: 0.4, reach: 15, range: 150 },
  rampOpen: { level: 0.3, reach: 15, range: 150 }, rampShut: { level: 0.3, reach: 15, range: 150 }, rampDown: { level: 0.55, reach: 25, range: 300 },
  boots: { level: 0.5, reach: 8, range: 60 }, warn: { level: 0.25, reach: 25, range: 200 },
  winch: { level: 0.35, reach: 20, range: 250 }, cableRelease: { level: 0.45, reach: 20, range: 250 }, clamps: { level: 0.45, reach: 20, range: 250 },
  podWhistle: { level: 0.45, reach: 60, range: 900 }, podImpact: { level: 0.85, reach: 50, range: 1200 }, podHatch: { level: 0.35, reach: 12, range: 120 },
};

/** An event `people` from the listener, on its `pan` side: its loudness, side and the air's cut (null past its range). Its level is × the ship's voice gain. */
export function hearDropshipFx(kind: DropshipEventKind, people: number, pan: number, voiceGain: number): { gain: number; pan: number; cutoff: number } | null {
  const k = FX_HEARD[kind];
  if (people > k.range) return null;
  return {
    gain: voiceGain * k.level / (1 + Math.max(0, people) / k.reach),
    pan: clamp(pan, -1, 1),
    cutoff: clamp(9000 * Math.exp(-Math.max(0, people) / 140), 240, 9000),
  };
}
